/**
 * Supabase free-tier guard — stay within free limits until 6 paying customers.
 * Media stays on Cloudflare R2; this module throttles Postgres egress + realtime.
 */
import { dataProvider as supabase } from "@/lib/providers/data-provider";

export const PAYING_CUSTOMER_THRESHOLD = 6;

export type PlatformScaleGuard = {
  payingCustomers: number;
  freeTierThreshold: number;
  conservativeMode: boolean;
  realtimeRecommended: boolean;
  maxDocumentShareDays: number;
};

const DEFAULT_GUARD: PlatformScaleGuard = {
  payingCustomers: 0,
  freeTierThreshold: PAYING_CUSTOMER_THRESHOLD,
  conservativeMode: true,
  realtimeRecommended: false,
  maxDocumentShareDays: 7,
};

let cachedGuard: PlatformScaleGuard = DEFAULT_GUARD;
let cachedAt = 0;
const CACHE_MS = 5 * 60_000;

function parseGuard(row: Record<string, unknown> | null): PlatformScaleGuard {
  if (!row) return DEFAULT_GUARD;
  const paying = Number(row.paying_customers ?? 0);
  const threshold = Number(row.free_tier_threshold ?? PAYING_CUSTOMER_THRESHOLD);
  const conservative = row.conservative_mode !== false && paying < threshold;
  return {
    payingCustomers: paying,
    freeTierThreshold: threshold,
    conservativeMode: conservative,
    realtimeRecommended: Boolean(row.realtime_recommended ?? paying >= threshold),
    maxDocumentShareDays: Number(row.max_document_share_days ?? (conservative ? 7 : 365)),
  };
}

export function getPlatformScaleGuardSync(): PlatformScaleGuard {
  return cachedGuard;
}

export function isConservativeSupabaseMode(): boolean {
  return cachedGuard.conservativeMode;
}

export async function refreshPlatformScaleGuard(force = false): Promise<PlatformScaleGuard> {
  if (!force && cachedAt && Date.now() - cachedAt < CACHE_MS) {
    return cachedGuard;
  }
  try {
    const { data, error } = await (supabase as any).rpc("get_platform_scale_guard");
    if (!error && data) {
      cachedGuard = parseGuard(data as Record<string, unknown>);
      cachedAt = Date.now();
      return cachedGuard;
    }
  } catch {
    // RPC may be unavailable during boot — keep conservative defaults
  }
  cachedGuard = DEFAULT_GUARD;
  cachedAt = Date.now();
  return cachedGuard;
}

export function maxDocumentShareDaysForCurrentScale(requested?: number): number {
  const cap = cachedGuard.maxDocumentShareDays;
  if (!requested || requested <= 0) return cap;
  return Math.min(requested, cap);
}
